import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, DollarSign, Clock } from 'lucide-react';
import { shiftService } from '../api/shifts';
import { payrollService } from '../api/payroll';
import { staffService } from '../../staff/api/mockRepo';
import type { Shift, PayrollEntry } from '../types';
import type { Staff } from '../../staff/types';

export default function HROverview() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [payroll, setPayroll] = useState<PayrollEntry[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [shiftsData, payrollData, staffData] = await Promise.all([
        shiftService.getAll(),
        payrollService.getAll(),
        staffService.getAll()
      ]);
      
      setShifts(shiftsData);
      setPayroll(payrollData);
      setStaff(staffData);
    } catch (error) {
      console.error('Failed to load HR data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      name: 'Total Staff',
      value: staff.filter(s => s.status === 'active').length.toString(),
      icon: Users,
      color: 'text-blue-600'
    },
    {
      name: 'Scheduled Shifts',
      value: shifts.filter(s => s.status === 'scheduled').length.toString(),
      icon: Calendar,
      color: 'text-green-600'
    },
    {
      name: 'Pending Payroll',
      value: payroll.filter(p => p.status === 'draft').length.toString(),
      icon: DollarSign,
      color: 'text-yellow-600'
    },
    {
      name: 'This Month Hours',
      value: shifts.reduce((sum, s) => {
        const start = new Date(`2024-01-01 ${s.start_time}`);
        const end = new Date(`2024-01-01 ${s.end_time}`);
        return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      }, 0).toFixed(0),
      icon: Clock,
      color: 'text-purple-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Human Resources</h1>
        <p className="text-sm text-neutral-600">Manage staff schedules, attendance, and payroll</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-lg border p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon className={`h-8 w-8 ${stat.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-neutral-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="text-lg font-medium text-neutral-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Link
          to="/hr/roster"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Staff Roster</h3>
              <p className="text-sm text-neutral-600">Manage staff schedules and shifts</p>
            </div>
          </div>
        </Link>

        <Link
          to="/hr/attendance"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Attendance</h3>
              <p className="text-sm text-neutral-600">Track staff attendance and hours</p>
            </div>
          </div>
        </Link>

        <Link
          to="/hr/payroll"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Payroll</h3>
              <p className="text-sm text-neutral-600">Process staff payroll and salaries</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
